import { useCallback, useEffect } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

/** What the page needs to guard its own navigation and to render the confirm dialog. */
interface UnsavedChangesGuard {
    isConfirmOpen: boolean;
    /** Take the page's own leave affordance through the guard. `leave` MUST be a router navigation — that is
     *  what the blocker holds; anything else (a location assignment, a state reset) is not guarded here. */
    confirmNavigate: (leave: () => void) => void;
    confirmLeave: () => void;
    cancelLeave: () => void;
}

// Keeps a dirty form from being lost (CALL-783), on all three ways out:
//  - a reload or a closed tab → the browser's own prompt (beforeunload);
//  - the page's own "leave" affordances → confirmNavigate;
//  - ANY in-app navigation, sidebar included → useBlocker.
//
// The sidebar is why useBlocker is needed rather than a click interceptor: SideDrawer navigates
// programmatically, so there is no anchor to catch. useBlocker only works under a data router, which is
// what src/index.js mounts (createBrowserRouter) for exactly this reason.
//
// The page's own exits are not held HERE, they are handed to the router like every other navigation, and the
// blocker holds them. They used to be parked in local state and re-run on confirm, which never worked on the
// first click: re-running them navigates, which trips the blocker (nothing has cleared isDirty), while the
// blocker object the confirm handler had closed over was still the idle one — `{state:'unblocked', proceed:
// undefined}`. `proceed` exists only on the object the router installs when the blocker becomes BLOCKED, so
// the navigation was dropped synchronously and never resumed; the second click, rendered against the blocked
// object, finally went through. Reading `blocker` fresh inside the handler would not have helped either:
// updateBlocker only schedules a React state update, so nothing in that tick can see the blocked object.
//
// With one mechanism there is no second pending state to keep in step, and no way to navigate away while
// dirty without passing the blocker first.
export const useUnsavedChangesGuard = (isDirty: boolean): UnsavedChangesGuard => {
    // Block only a real route change: a search-param edit on the same page (the consolidated view writes
    // its selection into the URL) must not raise a "discard your changes?" dialog.
    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
        [isDirty]
    );
    const blocker = useBlocker(shouldBlock);

    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            // Chrome only shows its native prompt when both are set.
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    // Just navigate: clean, it goes through; dirty, the blocker stops it and the dialog below is what the
    // user sees. The router keeps the pending navigation, so resuming it is blocker.proceed() and not a
    // second navigate() — which would be blocked all over again.
    const confirmNavigate = useCallback((leave: () => void) => leave(), []);

    const confirmLeave = useCallback(() => {
        blocker.proceed?.();
    }, [blocker]);

    const cancelLeave = useCallback(() => {
        blocker.reset?.();
    }, [blocker]);

    return {
        isConfirmOpen: blocker.state === 'blocked',
        confirmNavigate,
        confirmLeave,
        cancelLeave,
    };
};
